import React, { useState, useEffect, useCallback } from 'react';
import {
    Typography,
    Box,
    Tooltip,
    IconButton,
    Fade,
    Button,
    CircularProgress,
    TextField,
    MenuItem,
    Snackbar,
    useTheme,
    useMediaQuery
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import PublicIcon from '@mui/icons-material/Public';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';

const SettingAI = ({ caseid }) => {
    const { t } = useTranslation();
    const { ws } = useWebSocket();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isVisible, setIsVisible] = useState(false);

    const [projectModelProvider, setProjectModelProvider] = useState('');
    const [projectTextModelId, setProjectTextModelId] = useState('');
    const [projectTextModelProvider, setProjectTextModelProvider] = useState('');
    const [projectVisionModelId, setProjectVisionModelId] = useState('');
    const [projectVisionModelProvider, setProjectVisionModelProvider] = useState('');
    const [projectAudioModelId, setProjectAudioModelId] = useState('');
    const [projectAudioModelProvider, setProjectAudioModelProvider] = useState('');

    // Track original values so we can determine whether user changed anything
    const [initialProjectModel, setInitialProjectModel] = useState({
        provider: '',
        textProvider: '',
        textId: '',
        visionProvider: '',
        visionId: '',
        audioProvider: '',
        audioId: ''
    });
    const [projectModelDirty, setProjectModelDirty] = useState(false);
    const [savedModels, setSavedModels] = useState({ text: [], vision: [], audio: [] });
    const [projectModelSaving, setProjectModelSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Trigger animation when component mounts
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const loadSettings = useCallback(() => {
        if (ws) {
            try {
                ws.send(JSON.stringify({
                    command: 'get_saved_models'
                }));
                // Request case data
                ws.send(JSON.stringify({
                    command: 'getcase',
                    caseid: caseid
                }));
            } catch (error) {
                console.error('Error sending loadSettings commands:', error);
            }
        }
    }, [ws, caseid]);

    useEffect(() => {
        if (ws && caseid) {
            loadSettings();
        }
    }, [ws, caseid, loadSettings]);

    // Handle WebSocket messages
    useEffect(() => {
        if (!ws) return;

        const handleMessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.command === 'getcase') {
                    if (data.data && data.data.case) {
                        setProjectModelProvider(data.data.case.project_model_provider || '');
                        setProjectTextModelProvider(data.data.case.project_text_model_provider || '');
                        // Convert model IDs to strings to match dropdown values
                        setProjectTextModelId(data.data.case.project_text_model_id ? String(data.data.case.project_text_model_id) : '');
                        setProjectVisionModelProvider(data.data.case.project_vision_model_provider || '');
                        setProjectVisionModelId(data.data.case.project_vision_model_id ? String(data.data.case.project_vision_model_id) : '');
                        setProjectAudioModelProvider(data.data.case.project_audio_model_provider || '');
                        setProjectAudioModelId(data.data.case.project_audio_model_id ? String(data.data.case.project_audio_model_id) : '');

                        // Save initial model values for change detection
                        setInitialProjectModel({
                            provider: data.data.case.project_model_provider || '',
                            textProvider: data.data.case.project_text_model_provider || '',
                            textId: data.data.case.project_text_model_id ? String(data.data.case.project_text_model_id) : '',
                            visionProvider: data.data.case.project_vision_model_provider || '',
                            visionId: data.data.case.project_vision_model_id ? String(data.data.case.project_vision_model_id) : '',
                            audioProvider: data.data.case.project_audio_model_provider || '',
                            audioId: data.data.case.project_audio_model_id ? String(data.data.case.project_audio_model_id) : ''
                        });
                        setProjectModelDirty(false);
                    }
                } else if (data.command === 'saved_models') {
                    setSavedModels(data.data || { text: [], vision: [], audio: [] });
                } else if (data.command === 'update_project_model_provider') {
                    const payload = data.data || data;
                    setProjectModelSaving(false);
                    if (payload && payload.status === 'success') {
                        setProjectModelProvider(payload.project_model_provider || '');
                        setProjectTextModelProvider(payload.project_text_model_provider || '');
                        // Convert model IDs to strings to match dropdown values
                        setProjectTextModelId(payload.project_text_model_id ? String(payload.project_text_model_id) : '');
                        setProjectVisionModelProvider(payload.project_vision_model_provider || '');
                        setProjectVisionModelId(payload.project_vision_model_id ? String(payload.project_vision_model_id) : '');
                        setProjectAudioModelProvider(payload.project_audio_model_provider || '');
                        setProjectAudioModelId(payload.project_audio_model_id ? String(payload.project_audio_model_id) : '');
                        // After successful save, update the initial values and reset dirty flag
                        setInitialProjectModel({
                            provider: payload.project_model_provider || '',
                            textProvider: payload.project_text_model_provider || '',
                            textId: payload.project_text_model_id ? String(payload.project_text_model_id) : '',
                            visionProvider: payload.project_vision_model_provider || '',
                            visionId: payload.project_vision_model_id ? String(payload.project_vision_model_id) : '',
                            audioProvider: payload.project_audio_model_provider || '',
                            audioId: payload.project_audio_model_id ? String(payload.project_audio_model_id) : ''
                        });
                        setProjectModelDirty(false);
                        setSnackbar({
                            open: true,
                            message: t('settings.ai.project_model_update_success', 'Project AI model updated'),
                            severity: 'success'
                        });
                    } else {
                        const errorMessage = payload?.message || t('settings.ai.project_model_update_error', 'Failed to update project AI model');
                        setSnackbar({
                            open: true,
                            message: errorMessage,
                            severity: 'error'
                        });
                    }
                }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };

        ws.addEventListener('message', handleMessage);
        return () => ws.removeEventListener('message', handleMessage);
    }, [ws, t]);

    const handleProjectModelSave = () => {
        const valueToSave = (projectTextModelProvider || projectModelProvider || '').trim();
        setProjectModelSaving(true);

        if (ws) {
            try {
                ws.send(JSON.stringify({
                    command: 'update_project_model_provider',
                    caseid: caseid,
                    project_model_provider: valueToSave,
                    project_text_model_provider: projectTextModelProvider || '',
                    project_text_model_id: projectTextModelId || '',
                    project_vision_model_provider: projectVisionModelProvider || '',
                    project_vision_model_id: projectVisionModelId || '',
                    project_audio_model_provider: projectAudioModelProvider || '',
                    project_audio_model_id: projectAudioModelId || ''
                }));
            } catch (error) {
                console.error('Error sending update_project_model_provider:', error);
                setProjectModelSaving(false);
                setSnackbar({ open: true, message: t('settings.ai.project_model_update_error'), severity: 'error' });
            }
        } else {
            setProjectModelSaving(false);
            setSnackbar({ open: true, message: t('settings.others.websocket_missing', 'WebSocket connection unavailable'), severity: 'error' });
        }
    };

    const handleModelSelect = (type) => (event) => {
        const value = event.target.value;
        const modelList = savedModels?.[type] || [];
        const selectedModel = modelList.find((model) => String(model.id) === String(value));
        const provider = value ? (selectedModel?.provider || '') : '';

        if (type === 'text') {
            setProjectTextModelId(value);
            setProjectTextModelProvider(provider);
        } else if (type === 'vision') {
            setProjectVisionModelId(value);
            setProjectVisionModelProvider(provider);
        } else if (type === 'audio') {
            setProjectAudioModelId(value);
            setProjectAudioModelProvider(provider);
        }
    };

    // Recompute dirty flag whenever any of the model selection values change
    useEffect(() => {
        const dirty = (
            projectModelProvider !== initialProjectModel.provider ||
            projectTextModelProvider !== initialProjectModel.textProvider ||
            (projectTextModelId ? String(projectTextModelId) : '') !== (initialProjectModel.textId ? String(initialProjectModel.textId) : '') ||
            projectVisionModelProvider !== initialProjectModel.visionProvider ||
            (projectVisionModelId ? String(projectVisionModelId) : '') !== (initialProjectModel.visionId ? String(initialProjectModel.visionId) : '') ||
            projectAudioModelProvider !== initialProjectModel.audioProvider ||
            (projectAudioModelId ? String(projectAudioModelId) : '') !== (initialProjectModel.audioId ? String(initialProjectModel.audioId) : '')
        );
        setProjectModelDirty(dirty);
    }, [projectModelProvider, projectTextModelProvider, projectTextModelId, projectVisionModelProvider, projectVisionModelId, projectAudioModelProvider, projectAudioModelId, initialProjectModel]);

    const handleSnackbarClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <>
            <Fade in={true} timeout={600}>
                <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Typography variant={isMobile ? "h6" : "h5"} gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {t("settings.ai.title", "AI Settings")}
                        <Tooltip title={t("settings.ai.description", "Configure AI models for this project")}>
                            <IconButton size="small">
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Typography>
                </Box>
            </Fade>

            <Fade in={isVisible} timeout={800}>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={{ xs: 2, sm: 3 }}
                    sx={{
                        width: '100%',
                        maxWidth: '800px',
                        mx: 'auto',
                        px: { xs: 0, sm: 2 },
                    }}
                >
                    {/* Project AI model override */}
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '600px',
                            p: { xs: 2.5, sm: 4 },
                            borderRadius: { xs: 2, sm: 3 },
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
                            border: '1px solid rgba(87, 87, 176, 0.08)',
                            boxShadow: '0 4px 20px rgba(87, 87, 176, 0.08)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                boxShadow: '0 8px 30px rgba(87, 87, 176, 0.12)',
                                transform: 'translateY(-2px)',
                                borderColor: 'rgba(87, 87, 176, 0.12)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 } }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, #5757b0 0%, #7777d0 100%)',
                                    boxShadow: '0 4px 15px rgba(87, 87, 176, 0.3)',
                                    flexShrink: 0
                                }}
                            >
                                <PublicIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#2d3748', mb: 0.5, lineHeight: 1.2 }}>
                                    {t('settings.ai.project_model_title', 'Project AI model override')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.4, display: 'block' }}>
                                    {t('settings.ai.project_model_helper', 'Choose a specific AI model host for this project or fall back to the global default')}
                                </Typography>
                            </Box>
                        </Box>

                        <TextField
                            select
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            label={t('settings.ai.project_text_model_label', 'Preferred text model')}
                            value={projectTextModelId}
                            onChange={handleModelSelect('text')}
                            disabled={projectModelSaving}
                            helperText={t('settings.ai.project_model_hint', 'Applies to AI tasks such as title suggestions, descriptions, and widgets')}
                            sx={{ mb: { xs: 1.5, sm: 2 } }}
                            SelectProps={{
                                renderValue: (val) => {
                                    if (!val) return t('settings.ai.project_model_reset', 'Use Global Default');
                                    const model = (savedModels?.text || []).find((m) => String(m.id) === String(val));
                                    return model?.model_name || model?.provider || String(val);
                                }
                            }}
                        >
                            <MenuItem value="">{t('settings.ai.project_model_reset', 'Use global default')}</MenuItem>
                            {(savedModels?.text || []).map((model) => (
                                <MenuItem key={`text-${model.id}`} value={String(model.id)}>
                                    {model.model_name || model.provider}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            label={t('settings.ai.project_vision_model_label', 'Vision model')}
                            value={projectVisionModelId}
                            onChange={handleModelSelect('vision')}
                            disabled={projectModelSaving}
                            helperText={t('settings.ai.project_vision_model_hint', 'Used for image inputs in this project')}
                            sx={{ mb: { xs: 1.5, sm: 2 } }}
                            SelectProps={{
                                renderValue: (val) => {
                                    if (!val) return t('settings.ai.project_model_reset', 'Use Global Default');
                                    const model = (savedModels?.vision || []).find((m) => String(m.id) === String(val));
                                    return model?.model_name || model?.provider || String(val);
                                }
                            }}
                        >
                            <MenuItem value="">{t('settings.ai.project_model_reset', 'Use global default')}</MenuItem>
                            {(savedModels?.vision || []).map((model) => (
                                <MenuItem key={`vision-${model.id}`} value={String(model.id)}>
                                    {model.model_name || model.provider}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            fullWidth
                            size={isMobile ? "small" : "medium"}
                            label={t('settings.ai.project_audio_model_label', 'Audio model')}
                            value={projectAudioModelId}
                            onChange={handleModelSelect('audio')}
                            disabled={projectModelSaving}
                            helperText={t('settings.ai.project_audio_model_hint', 'Used for speech or audio analysis in this project')}
                            SelectProps={{
                                renderValue: (val) => {
                                    if (!val) return t('settings.ai.project_model_reset', 'Use Global Default');
                                    const model = (savedModels?.audio || []).find((m) => String(m.id) === String(val));
                                    return model?.model_name || model?.provider || String(val);
                                }
                            }}
                        >
                            <MenuItem value="">{t('settings.ai.project_model_reset', 'Use global default')}</MenuItem>
                            {(savedModels?.audio || []).map((model) => (
                                <MenuItem key={`audio-${model.id}`} value={String(model.id)}>
                                    {model.model_name || model.provider}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: { xs: 2.5, sm: 3 } }}>
                            <Button
                                variant="contained"
                                fullWidth={isMobile}
                                onClick={() => handleProjectModelSave()}
                                disabled={projectModelSaving || !projectModelDirty}
                                sx={{
                                    backgroundColor: '#5757b0',
                                    color: '#fff',
                                    fontWeight: 600,
                                    px: 2.5,
                                    '&:hover': { backgroundColor: '#7777d0' },
                                    minWidth: { xs: '100%', sm: 180 }
                                }}
                            >
                                {projectModelSaving ? (
                                    <CircularProgress size={18} sx={{ color: 'white' }} />
                                ) : (
                                    t('settings.ai.project_model_save', 'Save model preference')
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Fade>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                message={snackbar.message}
            />
        </>
    );
};

export default SettingAI;
